import Vue from 'vue';
import VueDOMPurifyHTML from 'vue-dompurify-html';
import {BootstrapVue, IconsPlugin} from 'bootstrap-vue';
import './assets/scss/theme.scss';
import Toast from 'vue-toastification';
import 'vue-toastification/dist/index.css';
import 'video.js/dist/video-js.css';
import Vuelidate from 'vuelidate';
import Meta from 'vue-meta';
import App from '@/App.vue';
import router from '@/router';
import store from '@/store';
import {onApiError, callLambda} from '@/lib/api/lambda';
import Message from '@/lib/base/message';
import PortalVue from 'portal-vue';
import '@/assets/icomoon/style.css';
import delay from '@/lib/base/delay';
import {removeCanvasOnDestroy} from '@/lib/base/canvas.js';
import {Device} from '@capacitor/device';
import filters from './lib/base/filters';
import JPush from '@/lib/jpush';
import JShare from '@/lib/jshare';
import SoundService from '@/lib/base/soundService';
import AppEvent from '@/lib/base/appEvent.js';
import './lib/plugins/device';
import './lib/plugins/directives';
import day from 'dayjs';
import './lib/plugins/locale';
import VueSlider from 'vue-slider-component';
import 'vue-slider-component/theme/default.css';
import notificationHandler from '@/lib/base/notificationHandler.js';
import VCalendar from 'v-calendar';
import {stage, gtm} from '@/constant/env';
import VueGtm from '@gtm-support/vue2-gtm';
import {dragScroll} from '@/directives/scroll.js';
import VueCroppie from 'vue-croppie';
import 'croppie/croppie.css';
import InAppPurchase from '@/lib/base/inAppPurchase';
import alertFunction from '@/lib/base/alertFunction.js';
import openHomePage from '@/lib/base/openHomePage';
import i18n from '@/i18n/index.js';
import {translateByLocale} from '@/i18n/utils.js';
import ICP from '@/components/Base/ICP.vue';

if (stage === 'prod') {
  import('@/lib/base/fbPixel.js');
  import('@/lib/analytics');
  Vue.prototype.$gaTrack = async (event, options) => {
    try {
      await Vue.prototype.$gtag.event(event, options);
    } catch (error) {
      console.log(error);
    }
  };
} else {
  Vue.prototype.$gaTrack = (event, options) => {
    console.log({event, options});
  };
}

/**
 * 設定螢幕方向
 * @param {String} orientation 方向
 */
const setScreenOrientation = async (orientation) => {
  try {
    await window.screen.orientation.lock(orientation);
    // eslint-disable-next-line no-empty
  } catch (_) {}
};

if (Vue.prototype.$device.isTablet) {
  setScreenOrientation('landscape');
} else {
  setScreenOrientation('portrait');
}

Object.keys(filters).forEach((key) => {
  Vue.filter(key, filters[key]);
});

if (!window.isUnitTest) {
  require('wgo.js/wgo/wgo.js');
  require('wgo.js/wgo/kifu.js');
  require('wgo.js/wgo/sgfparser.js');
  require('wgo.js/wgo/player.js');
  require('wgo.js/wgo/basicplayer.js');
  require('wgo.js/wgo/basicplayer.component.js');
  require('wgo.js/wgo/basicplayer.infobox.js');
  require('wgo.js/wgo/basicplayer.commentbox.js');
  require('wgo.js/wgo/basicplayer.control.js');
  require('wgo.js/wgo/player.editable.js');
  require('wgo.js/wgo/scoremode.js');
  require('wgo.js/wgo/player.permalink.js');
}

Vue.config.productionTip = false;

onApiError(function (
  error,
  {category, apiName, data, params, headers, method}
) {
  if (
    error.response &&
    error.response.status === 401 &&
    apiName !== 'hjjPushTags'
  ) {
    const url = new URL(window.location.href);
    if (
      !url.href.includes('reset-password') &&
      !url.href.includes('signup-status')
    ) {
      store.dispatch('user/logout');
    }
  }
  if (error.response && error.response.status === 503) {
    router.push({name: 'maintenance'});
  }
  if (error && ['ECONNRESET', 'ETIMEDOUT'].includes(error.code)) {
    return delay(25).then(() => {
      return callLambda({category, apiName, data, params, headers, method});
    });
  }
});

Vue.use(VueDOMPurifyHTML);
// Make BootstrapVue available throughout your project
Vue.use(BootstrapVue, {
  BButton: {
    size: [
      'oval-lg',
      'oval-sm',
      'fixed-lg',
      'fixed-md',
      'fixed-sm',
      'sm',
      'md',
      'lg',
    ],
  },
});
// Optionally install the BootstrapVue icon components plugin
Vue.use(IconsPlugin);
Vue.use(Toast);
Vue.use(Vuelidate);
Vue.use(Meta);
Vue.use(PortalVue);
Vue.component('VueSlider', VueSlider);
Vue.use(VCalendar, {
  componentPrefix: 'vc', // Use <vc-calendar /> instead of <v-calendar />
});
Vue.use(VueCroppie);
Vue.use(translateByLocale);

if (gtm && stage === 'prod' && process.env.NODE_ENV !== 'development') {
  Vue.use(VueGtm, {
    id: gtm,
    defer: false,
    compatibility: true,
    nonce: '2726c7f26c',
    enabled: true,
    debug: process.env.NODE_ENV === 'development',
    loadScript: true,
    vueRouter: router,
    trackOnNextTick: false,
  });
  Vue.prototype.$gtmTrack = (event) => {
    try {
      if (Array.isArray(window.dataLayer)) window.dataLayer?.push({event});
    } catch (error) {
      console.log(error);
    }
  };
} else {
  Vue.prototype.$gtmTrack = (event) => {
    console.log('DEV GTM', {event});
  };
}

Vue.prototype.$t = function (key, values) {
  let result = '';
  if (this.$i18n) {
    result = this.$i18n.t(key, values);
  }
  return result;
};
Vue.prototype.$day = day;
Vue.prototype.$Message = Message;
Vue.prototype.$JShare = new JShare();
const jpush = new JPush();
Vue.prototype.$JPush = jpush;
Vue.prototype.setScreenOrientation = setScreenOrientation;
AppEvent.on(AppEvent.eventNameEnum.APP_URL_OPEN, (data) => {
  let url = new URL(data.url);
  if (url.searchParams.has('al_applink_data')) {
    const applinkData = JSON.parse(url.searchParams.get('al_applink_data'));
    url = new URL(applinkData.target_url);
  } else if (url.searchParams.has('$deeplink_path')) {
    const deepLinkPath = url.searchParams.get('$deeplink_path');
    url = new URL(`http://${decodeURIComponent(deepLinkPath)}`);
  }
  if (url.searchParams.has('pvpId')) {
    const pvpId = url.searchParams.get('pvpId');
    router.push({name: 'pvp-join', params: {id: pvpId}});
  } else if (url.searchParams.has('spectateId')) {
    const spectateId = url.searchParams.get('spectateId');
    router.push({name: 'home', query: {spectateId}});
  } else if (url.searchParams.has('email')) {
    // todo: 2022/10/5上線版本暫時不使用branch.io的網址，但後續可能會調整流程有可能還是會使用，故先保留
    const type = url.searchParams.get('email');
    const id = url.searchParams.get('id');
    const code = url.searchParams.get('code');
    router.push({name: type, query: {id, code, type: 'email'}});
  } else if (url.href.includes('/liveList')) {
    router.push({name: 'liveList'});
  } else if (url.hash) {
    router.push({path: url.hash.replace('#', '')});
  }
});

/**
 * 點擊推波事件處理
 * @param {Object} info 點擊推波內容
 */
const clickNotificationHandler = async (info) => {
  jpush.clearTempInfo();
  notificationHandler(info);
};

Device.getInfo().then(async (deviceInfo) => {
  if (deviceInfo.platform !== 'web') {
    jpush.updateTags();
    AppEvent.on(AppEvent.eventNameEnum.APP_STATE_CHANGE, () => {
      jpush.updateTags();
    });
    jpush.capacitorPlugin.addListener('onClickNotification', (info) => {
      clickNotificationHandler(info);
    });
    const info = await jpush.getTempInfo();
    clickNotificationHandler(info);
  } else if (deviceInfo.platform === 'web') {
    const url = new URL(window.location.href);
    if (url.searchParams.has('pvpId')) {
      const pvpId = url.searchParams.get('pvpId');
      window.location.href = window.location.origin + `#/pvp-join/${pvpId}`;
    }
    if (url.searchParams.has('spectateId')) {
      const spectateId = url.searchParams.get('spectateId');
      window.location.href =
        window.location.origin + `#/?spectateId=${spectateId}`;
    }
    if (url.searchParams.has('email')) {
      const type = url.searchParams.get('email');
      const id = url.searchParams.get('id');
      const code = url.searchParams.get('code');
      window.location.href =
        window.location.origin + `#/${type}/?id=${id}&code=${code}&type=email`;
    }
    if (url.searchParams.has('kifuId')) {
      const kifuId = url.searchParams.get('kifuId');
      const gameMode = url.searchParams.get('gameMode');
      const username = url.searchParams.get('username');
      window.location.href =
        window.location.origin +
        `#/personal/kifu/${kifuId}/${gameMode}?username=${username}`;
    }
  }
});

Vue.prototype.$playSound = (...args) => {
  SoundService.playSound(...args);
  return true;
};
Vue.prototype.$alert = alertFunction;

Vue.prototype.$eventBus = new Vue();

Vue.prototype.$openHomePage = async function (path = '/') {
  let isNoLogin = false;
  if (typeof path === 'object') {
    path = path.path;
    isNoLogin = path.isNoLogin;
  }
  this.$store.commit('env/setIsLoading', true);
  const username = this.$store.getters['user/userData']?.username;
  await openHomePage({path, isNoLogin, username});
  this.$store.commit('env/setIsLoading', false);
};

Vue.mixin({
  beforeDestroy() {
    removeCanvasOnDestroy(this.$el);
  },
});

// custom directives
Vue.directive('drag-scroll', dragScroll);
Vue.component('ICP', ICP);
new Vue({
  i18n,
  router,
  store,
  render: (h) => h(App),
}).$mount('#app');

document.addEventListener('deviceready', () => InAppPurchase.init(), false);
